import { z } from 'zod';
import { uuid } from './posting.js';
/**
 * Der Vertrag des E-Mail-Eingangs — Auftrag `EM-konto`, Spec
 * `docs/superpowers/specs/2026-11-04-email-eingang-design.md` Abschnitte 4, 7
 * und 8 in der Fassung des Nachtrags 0.4.
 *
 * **Ein Passwort steht in keiner Antwort.** Es gibt kein Antwortschema mit
 * einem Feld dafür — nur `passwortGesetztAm` (`D-2`, `E-2168`). Die Rümpfe
 * tragen es schreibend: beim Anlegen als Pflicht, beim Ändern wahlfrei (und
 * Pflicht, sobald Server, Port oder Benutzer wechseln — das prüft der Dienst,
 * weil es vom gespeicherten Stand abhängt, Nachtrag 0.4 Nr. 7).
 *
 * **Die Anhangsliste des Protokolls trägt die Schlüssel der Datenbank**
 * (`incoming_document_id`, Spec 4.5): Sie ist ein Protokolleintrag und wird
 * unverändert ausgeliefert, damit Ablage und Anzeige dieselbe Form lesen.
 */
const zeitpunkt = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/, 'Zeitpunkt in ISO-8601 UTC erwartet');
/* ==========================================================================
 * Aufzählungen
 * ========================================================================== */
/** `D-1`: TLS (Vorgabe, Port 993) oder STARTTLS (Port 143). */
export const mailVerschluesselungSchema = z.enum(['tls', 'starttls']);
/** `D-7`: die drei Modi je Postfach. */
export const mailKontoModusSchema = z.enum(['whitelist', 'regeln', 'whitelist_und_regeln']);
/** `D-8`: Aktion nach der Verarbeitung — kein Löschen. */
export const mailKontoAktionSchema = z.enum(['markieren', 'verschieben']);
/**
 * Der Zustand eines Postfachs, wie die Liste ihn zeigt: `inaktiv`, wenn
 * abgeschaltet; `fehler`, wenn der letzte Lauf scheiterte; sonst `aktiv`.
 */
export const mailKontoZustandSchema = z.enum(['aktiv', 'inaktiv', 'fehler']);
/** Das Ergebnis je Nachricht (Spalte `mail_abruf_nachricht.ergebnis`). */
export const mailAbrufErgebnisSchema = z.enum([
    'abgelegt',
    'dublette_nachricht',
    'uebersprungen_absender',
    'uebersprungen_keine_regel',
    'uebersprungen_kein_anhang',
    'fehler',
]);
/** Das Ergebnis je Anhang — dieselben Werte wie `AnhangErgebnis` in `apps/api/src/maileingang/typen.ts`. */
export const mailAnhangErgebnisSchema = z.enum(['abgelegt', 'dublette_anhang', 'nicht_zaehlbar', 'fehler']);
/** Die Art eines Verbindungsfehlers — dieselben Werte wie `MailKlientFehlerArt` in `imap-klient.ts`. */
export const mailKlientFehlerArtSchema = z.enum(['verbindung', 'anmeldung', 'ordner', 'uebertragung']);
/* ==========================================================================
 * Postfach
 * ========================================================================== */
const kontoName = z.string().trim().min(1, 'Name fehlt').max(80);
const host = z
    .string()
    .trim()
    .min(1, 'Server fehlt')
    .max(253)
    .regex(/^[^\s/]+$/, 'Der Server ist ein Name oder eine Adresse ohne Leerzeichen und ohne Pfad');
const port = z.number().int().min(1).max(65535);
const benutzer = z.string().trim().min(1, 'Benutzer fehlt').max(320);
/** Ein Passwort wird nie gekürzt oder getrimmt — Leerzeichen können dazugehören. */
const passwort = z.string().min(1, 'Passwort fehlt').max(1000);
const ordner = z.string().trim().min(1, 'Ordner fehlt').max(200);
const alterTage = z.number().int().min(1).max(3650);
/** Die Zahlen der letzten sieben Tage je Postfach (Sicht `mail_abruf_offen`). */
export const mailKontoZahlenSchema = z
    .object({
    abgelegt: z.number().int().nonnegative(),
    uebersprungen: z.number().int().nonnegative(),
    dubletten: z.number().int().nonnegative(),
    fehler: z.number().int().nonnegative(),
})
    .strict();
/** Ein Postfach in der Antwort — **ohne** Passwort (`D-2`, `E-2168`). */
export const mailKontoSchema = z
    .object({
    id: uuid,
    name: z.string().min(1),
    host: z.string().min(1),
    port,
    verschluesselung: mailVerschluesselungSchema,
    zertifikatPruefen: z.boolean(),
    benutzer: z.string().min(1),
    passwortGesetztAm: zeitpunkt,
    ordner: z.string().min(1),
    modus: mailKontoModusSchema,
    nurUngelesene: z.boolean(),
    maximalesAlterTage: z.number().int().positive(),
    aktion: mailKontoAktionSchema,
    zielordner: z.string().nullable(),
    aktiv: z.boolean(),
    zustand: mailKontoZustandSchema,
    letzterLaufBegonnen: zeitpunkt.nullable(),
    letzterLaufBeendet: zeitpunkt.nullable(),
    naechsterLaufAb: zeitpunkt,
    letzterFehler: z.string().nullable(),
    letzterFehlerAm: zeitpunkt.nullable(),
    fehlerInFolge: z.number().int().nonnegative(),
    letzte7Tage: mailKontoZahlenSchema,
    angelegtAm: zeitpunkt,
    geaendertAm: zeitpunkt.nullable(),
})
    .strict();
export const mailKontoListeSchema = z.object({ konten: z.array(mailKontoSchema) }).strict();
/** Anlegen: Passwort Pflicht; Zielordner Pflicht bei `verschieben`. */
export const mailKontoAnlageSchema = z
    .object({
    name: kontoName,
    host,
    port: port.default(993),
    verschluesselung: mailVerschluesselungSchema.default('tls'),
    zertifikatPruefen: z.boolean().default(true),
    benutzer,
    passwort,
    ordner: ordner.default('INBOX'),
    modus: mailKontoModusSchema,
    nurUngelesene: z.boolean().default(true),
    maximalesAlterTage: alterTage.default(30),
    aktion: mailKontoAktionSchema.default('markieren'),
    zielordner: ordner.nullable().optional(),
})
    .strict()
    .superRefine((wert, ctx) => {
    if (wert.aktion === 'verschieben' && (wert.zielordner === undefined || wert.zielordner === null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['zielordner'],
            message: 'Zum Verschieben braucht es einen Zielordner',
        });
    }
});
/**
 * Ändern: jedes Feld wahlfrei. `passwort` fehlt = unverändert — ausser Server,
 * Port oder Benutzer wechseln; dann verlangt der Dienst es
 * (`MAIL_KONTO_PASSWORT_FEHLT`, Nachtrag 0.4 Nr. 7).
 */
export const mailKontoPatchSchema = z
    .object({
    name: kontoName.optional(),
    host: host.optional(),
    port: port.optional(),
    verschluesselung: mailVerschluesselungSchema.optional(),
    zertifikatPruefen: z.boolean().optional(),
    benutzer: benutzer.optional(),
    passwort: passwort.optional(),
    ordner: ordner.optional(),
    modus: mailKontoModusSchema.optional(),
    nurUngelesene: z.boolean().optional(),
    maximalesAlterTage: alterTage.optional(),
    aktion: mailKontoAktionSchema.optional(),
    zielordner: ordner.nullable().optional(),
    aktiv: z.boolean().optional(),
})
    .strict();
/* ==========================================================================
 * Absenderliste
 * ========================================================================== */
/** Eine Adresse (`a@b.de`) oder eine Domain (`@b.de`); klein (CHECK in `0353`). */
export const mailAbsenderMusterSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(320)
    .regex(/^(@|[^@\s]+@)[^@\s]+$/, 'Eine Adresse (name@beispiel.de) oder eine Domain (@beispiel.de)');
export const mailKontoAbsenderSchema = z
    .object({
    id: uuid,
    kontoId: uuid,
    muster: z.string().min(1),
    bemerkung: z.string().nullable(),
    angelegtAm: zeitpunkt,
})
    .strict();
export const mailKontoAbsenderListeSchema = z
    .object({ absender: z.array(mailKontoAbsenderSchema) })
    .strict();
export const mailKontoAbsenderAnlageSchema = z
    .object({
    muster: mailAbsenderMusterSchema,
    bemerkung: z.string().trim().max(500).nullable().optional(),
})
    .strict();
/* ==========================================================================
 * Regeln
 * ========================================================================== */
/** Für wen eine Regel gilt: `'alle'` oder die Kennungen der Postfächer (`D-11`). */
export const mailRegelKontenSchema = z.union([z.literal('alle'), z.array(uuid).max(200)]);
/** Ein Filter — Teilzeichenkette oder Platzhaltermuster mit `*`, nie ein regulärer Ausdruck (`D-12`, `D-15`). */
const filter = z.string().max(500);
export const mailRegelSchema = z
    .object({
    id: uuid,
    name: z.string().min(1),
    aktiv: z.boolean(),
    reihenfolge: z.number().int(),
    kontoIds: mailRegelKontenSchema,
    absenderFilter: z.string(),
    empfaengerFilter: z.string(),
    betreffFilter: z.string(),
    inhaltFilter: z.string(),
    anhangEinschliessen: z.string(),
    anhangAusschliessen: z.string(),
    bilderErlauben: z.boolean(),
    maximalesAlterTage: z.number().int().positive().nullable(),
    vendorId: uuid.nullable(),
    bemerkung: z.string().nullable(),
    angelegtAm: zeitpunkt,
    geaendertAm: zeitpunkt.nullable(),
})
    .strict();
export const mailRegelListeSchema = z.object({ regeln: z.array(mailRegelSchema) }).strict();
/** Anlegen: leere Filter heissen „ohne“; ohne `reihenfolge` kommt die Regel ans Ende. */
export const mailRegelAnlageSchema = z
    .object({
    name: z.string().trim().min(1, 'Name fehlt').max(80),
    kontoIds: mailRegelKontenSchema,
    reihenfolge: z.number().int().min(-1_000_000).max(1_000_000).optional(),
    absenderFilter: filter.default(''),
    empfaengerFilter: filter.default(''),
    betreffFilter: filter.default(''),
    inhaltFilter: filter.default(''),
    anhangEinschliessen: filter.default(''),
    anhangAusschliessen: filter.default(''),
    bilderErlauben: z.boolean().default(false),
    maximalesAlterTage: alterTage.nullable().optional(),
    vendorId: uuid.nullable().optional(),
    bemerkung: z.string().trim().max(1000).nullable().optional(),
})
    .strict();
/** Ändern: jedes Feld wahlfrei, auch Zuordnung (`kontoIds` ersetzt), Reihenfolge und Zustand. */
export const mailRegelPatchSchema = z
    .object({
    name: z.string().trim().min(1, 'Name fehlt').max(80).optional(),
    kontoIds: mailRegelKontenSchema.optional(),
    reihenfolge: z.number().int().min(-1_000_000).max(1_000_000).optional(),
    absenderFilter: filter.optional(),
    empfaengerFilter: filter.optional(),
    betreffFilter: filter.optional(),
    inhaltFilter: filter.optional(),
    anhangEinschliessen: filter.optional(),
    anhangAusschliessen: filter.optional(),
    bilderErlauben: z.boolean().optional(),
    maximalesAlterTage: alterTage.nullable().optional(),
    vendorId: uuid.nullable().optional(),
    bemerkung: z.string().trim().max(1000).nullable().optional(),
    aktiv: z.boolean().optional(),
})
    .strict();
/* ==========================================================================
 * Protokoll
 * ========================================================================== */
/** Ein Anhang im Protokoll — die Form aus Spec 4.5, Schlüssel wie in der Datenbank. */
export const mailAnhangProtokollEintragSchema = z
    .object({
    dateiname: z.string(),
    medientyp: z.string(),
    bytes: z.number().int().nonnegative(),
    ergebnis: mailAnhangErgebnisSchema,
    incoming_document_id: uuid.optional(),
})
    .strict();
/** Die Spalte `mail_abruf_nachricht.anhaenge` — der Lesezod für `sql:jsonb`. */
export const mailAnhangProtokollSchema = z.array(mailAnhangProtokollEintragSchema);
/** Eine Protokollzeile — **ohne** Mailtext (den gibt es in der Tabelle nicht). */
export const mailAbrufNachrichtSchema = z
    .object({
    id: uuid,
    kontoId: uuid,
    kontoName: z.string(),
    uidvalidity: z.number().int().nonnegative(),
    uid: z.number().int().nonnegative(),
    messageId: z.string().nullable(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    absender: z.string().nullable(),
    betreff: z.string().nullable(),
    empfangenAm: zeitpunkt.nullable(),
    verarbeitetAm: zeitpunkt,
    ergebnis: mailAbrufErgebnisSchema,
    regelId: uuid.nullable(),
    regelName: z.string().nullable(),
    anhaenge: mailAnhangProtokollSchema,
    fehler: z.string().nullable(),
    aktionErgebnis: z.string().nullable(),
})
    .strict();
export const mailAbrufProtokollSchema = z
    .object({ zeilen: z.array(mailAbrufNachrichtSchema) })
    .strict();
/** Höchstzahl der Zeilen einer Protokollantwort (Spec 6, Reiter Protokoll). */
export const MAIL_PROTOKOLL_ZEILEN_MAX = 200;
/**
 * Die Abfrage von `GET /protokoll`: Postfach, Ergebnis und ein Beginn
 * (`JJJJ-MM-TT` oder ein Zeitpunkt in UTC), jedes wahlfrei.
 */
export const mailAbrufProtokollAbfrageSchema = z
    .object({
    konto: uuid.optional(),
    ergebnis: mailAbrufErgebnisSchema.optional(),
    seit: z.union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum JJJJ-MM-TT erwartet'), zeitpunkt]).optional(),
})
    .strict();
/* ==========================================================================
 * Verbindungsprüfung (Routen kommen mit dem Abruf, Spec 7, `D-17`)
 * ========================================================================== */
/** Rumpf von `POST /konten/pruefen`: der Zugang mit Passwort, vor dem Speichern. */
export const mailKontoPruefungRumpfSchema = z
    .object({
    host,
    port: port.default(993),
    verschluesselung: mailVerschluesselungSchema.default('tls'),
    zertifikatPruefen: z.boolean().default(true),
    benutzer,
    passwort,
    ordner: ordner.default('INBOX'),
})
    .strict();
export const mailKontoPruefungSchema = z
    .object({
    erfolgreich: z.boolean(),
    ordner: z.array(z.string()),
    nachrichtenImOrdner: z.number().int().nonnegative().nullable(),
    fehler: z
        .object({ art: mailKlientFehlerArtSchema, satz: z.string() })
        .strict()
        .optional(),
})
    .strict();
//# sourceMappingURL=maileingang.js.map